import React, { useState } from 'react';
import {
  Search,
  UserPlus,
  Upload,
  Link,
  Download,
  Users,
  Building2,
  MapPin,
  Phone,
  Mail,
  X,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

interface Client {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  createdAt: string;
  source: string;
}

const mockClients: Client[] = [
  {
    id: 'C001',
    name: 'John Doe',
    company: 'Belfius',
    email: 'john.doe@belfius.be',
    phone: '+32 123 456 789',
    address: 'Brussels, Belgium',
    createdAt: '2025-02-20',
    source: 'Manual'
  }
];

const crmSystems = [
  {
    name: 'HubSpot',
    icon: 'https://guurrickmxxnxobbsjbp.supabase.co/storage/v1/object/public/Images/CRM/hubspot.svg',
    description: 'Connect with HubSpot CRM to sync contacts and companies'
  },
  {
    name: 'Salesforce',
    icon: 'https://guurrickmxxnxobbsjbp.supabase.co/storage/v1/object/public/Images/CRM/salesforce.svg',
    description: 'Import contacts and leads from Salesforce'
  },
  {
    name: 'Teamleader',
    icon: 'https://guurrickmxxnxobbsjbp.supabase.co/storage/v1/object/public/Images/CRM/teamleader.svg',
    description: 'Sync with Teamleader contacts and companies'
  },
  {
    name: 'Monday.com',
    icon: 'https://guurrickmxxnxobbsjbp.supabase.co/storage/v1/object/public/Images/CRM/monday.svg',
    description: 'Connect with Monday.com boards and items'
  },
  {
    name: 'Odoo',
    icon: 'https://guurrickmxxnxobbsjbp.supabase.co/storage/v1/object/public/Images/CRM/odoo.svg',
    description: 'Integrate with Odoo CRM and contacts'
  }
];

export function ClientManagement() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isIntegrationsModalOpen, setIsIntegrationsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: ''
  });

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    // Handle client creation
    console.log('Creating client:', formData);
    setIsCreateModalOpen(false);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    
    setUploadStatus('uploading');
    
    // Simulate file upload
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setUploadStatus('success');
    
    // Reset after success
    setTimeout(() => {
      setUploadStatus('idle');
      setSelectedFile(null);
      setIsImportModalOpen(false);
    }, 1500);
  };

  const downloadTemplate = () => {
    // In a real app, this would trigger the download of a CSV template
    const template = 'Name,Company,Email,Phone,Address\nJohn Doe,Company Name,john@example.com,+32123456789,"Street, City, Country"';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'client_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-600 mt-1">Manage your client database</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
          >
            <Upload className="h-5 w-5" />
            <span>Import</span>
          </button>
          <button
            onClick={() => setIsIntegrationsModalOpen(true)}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
          >
            <Link className="h-5 w-5" />
            <span>Integrations</span>
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            <UserPlus className="h-5 w-5" />
            <span>Add Client</span>
          </button>
        </div>
      </div>

      {/* Client List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Client</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Contact</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Location</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Source</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {mockClients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{client.name}</div>
                        <div className="text-sm text-gray-500">{client.company}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="h-4 w-4 mr-2" />
                        {client.email}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="h-4 w-4 mr-2" />
                        {client.phone}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-2" />
                      {client.address}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {client.source}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {new Date(client.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Client Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Add New Client</h2>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateClient} className="p-6 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  Address
                </label>
                <input
                  type="text"
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
                >
                  Create Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Import Clients</h2>
              <button
                onClick={() => setIsImportModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Download Template</h3>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 text-jaimy-red hover:text-jaimy-red-dark transition-colors"
                >
                  <Download className="h-5 w-5" />
                  <span>Download CSV Template</span>
                </button>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Upload CSV File</h3>
                <div className="mt-2">
                  <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                    <div className="space-y-1 text-center">
                      <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer rounded-md font-medium text-jaimy-red hover:text-jaimy-red-dark focus-within:outline-none"
                        >
                          <span>Upload a file</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            accept=".csv"
                            className="sr-only"
                            onChange={handleFileUpload}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">CSV files only</p>
                    </div>
                  </div>
                </div>
                {selectedFile && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                    <FileSpreadsheet className="h-5 w-5" />
                    {selectedFile.name}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsImportModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={!selectedFile || uploadStatus === 'uploading'}
                  className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                >
                  {uploadStatus === 'uploading' ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Importing...
                    </>
                  ) : uploadStatus === 'success' ? (
                    <>
                      <CheckCircle2 className="h-5 w-5" />
                      Imported Successfully
                    </>
                  ) : uploadStatus === 'error' ? (
                    <>
                      <AlertCircle className="h-5 w-5" />
                      Import Failed
                    </>
                  ) : (
                    'Import Clients'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integrations Modal */}
      {isIntegrationsModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">CRM Integrations</h2>
              <button
                onClick={() => setIsIntegrationsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-4">
                {crmSystems.map((crm) => (
                  <div
                    key={crm.name}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-jaimy-red transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 flex items-center justify-center">
                        <img src={crm.icon} alt={crm.name} className="h-8 w-8" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{crm.name}</h3>
                        <p className="text-sm text-gray-600">{crm.description}</p>
                      </div>
                    </div>
                    <button className="px-4 py-2 text-jaimy-red hover:bg-jaimy-red/5 rounded-lg transition-colors">
                      Connect
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}